/* ===
   Campaign Direct — order flow widget logic
   Build theme 152943952070 only. Markup and copy live in
   blocks/cd-order-flow.liquid; everything below is behaviour.

   Spec: claude/order-flow-spec-v1.md
   CORRECTIONS THAT OVERRIDE IT: claude/order-flow-verification-2026-08-21.md

   ── The one thing a future editor must not get wrong ──

   THE VETO IS ON `click`, NOT ON `submit`.
   Verified 21 Aug on the live build theme: the Add to cart path fires NO submit
   event at all. Horizon declares on:submit="/handleSubmit" on
   <product-form-component>, but Apippa's own click handler bypasses it and
   submits the form natively, which by specification does not fire the submit
   event. A capture-phase submit listener — which is what §2.1 rule 3 originally
   called for — waits for a signal that never arrives, and every gate silently
   hangs open. The listener below is delegated on `document` in the CAPTURE
   phase so it runs ahead of Apippa's handler whatever order the scripts loaded
   in, survives the button being re-rendered, and covers the mobile sticky
   add-to-cart bar without needing to know it exists.

   ── The second thing ──

   THE GATES MATCH OPTION VALUES AS EXACT STRINGS. 'Campaign Direct designs it
   for me', 'Campaign Direct sources the list for me', 'Non-profit', 'First
   class', 'Use my own permit', and prefix tests on 'Split drop', 'Print and
   mail' and 'Rush'. Those strings live in the Liquid file's option values and
   are deliberately NOT theme settings. Change one there without changing it
   here and the route-out stops firing — silently, with no error, on a live
   product page. The controls became dropdowns on 22 Aug and the values were
   carried across unchanged for exactly this reason.
   === */

(function () {
  'use strict';

  /* Cart state, needed for the "mail orders stand alone" rule (§7.2 case 9),
     which depends on what is ALREADY in the cart. Cached because the veto has to
     decide synchronously — a fetch cannot happen inside preventDefault().

     STALENESS, and why it self-heals. If the cart changes without a page load
     (a second tab, a cart drawer, a back-forward restore) this cache goes stale,
     and a stale "there is a mail line in the cart" would block a customer from
     buying anything at all. That is the worst failure this widget could have, so
     it is guarded three ways: refresh on return to the page, refresh whenever
     the customer touches the widget, and — the important one — refresh
     immediately after ANY block, so a wrong block costs one click and then
     corrects itself rather than trapping the customer. Found by testing on
     21 Aug: clearing the cart without reloading left the widget refusing adds. */
  var cart = { count: 0, hasMailLine: false };

  function refreshCart() {
    if (!window.fetch) return;
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) {
        cart.count = c.item_count || 0;
        cart.hasMailLine = (c.items || []).some(function (i) {
          var v = (i.properties || {})['Mail Choice'];
          return !!v && String(v).indexOf('Print and mail') === 0;
        });
      })
      .catch(function () { /* leave the cached values alone */ });
  }

  /* The form. Selected BY CLASS, never by traversing from the calculator — they
     are siblings, not nested. May not exist yet when this runs (Apippa draws
     ~3s after load), so every use re-queries rather than caching a node. */
  function form() {
    return document.querySelector('form.cpc-calculator-form') ||
           document.querySelector('form[action*="/cart/add"]');
  }

  function num(n) {
    return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function init(root) {
    if (root.dataset.cdofInit) return;
    root.dataset.cdofInit = '1';

    var CFG;
    try { CFG = JSON.parse(root.querySelector('[data-cdof-config]').textContent); }
    catch (e) { return; }

    var errEl   = root.querySelector('[data-cdof-error]');
    var routeEl = root.querySelector('[data-cdof-routeout]');

    /* Every choice on this widget is a <select> as of 22 Aug. The radio branch
       below is kept on purpose and costs one line: this function is the SINGLE
       place the veto learns what the customer chose, so if a control is ever
       changed back to radios — or a new one arrives as radios — gating keeps
       working instead of quietly reading '' and letting everything through.
       An unanswered dropdown returns '' from its blank first option, which is
       what check() tests for. */
    function picked(n) {
      var sel = root.querySelector('select[data-cdof-' + n + ']');
      if (sel) return sel.value;
      var r = root.querySelector('[data-cdof-' + n + ']:checked');
      return r ? r.value : '';
    }
    function fieldVal(n) {
      var el = root.querySelector('[data-cdof-' + n + ']');
      return el ? String(el.value).trim() : '';
    }

    function artwork()    { return picked('artwork'); }
    function turnaround() { return picked('turnaround'); }
    function mailChoice() { return picked('mailchoice'); }
    function mailData()   { return picked('maildata'); }
    function mailDrops()  { return picked('maildrops'); }
    function mailClass()  { return picked('mailclass'); }
    function mailPermit() { return picked('mailpermit'); }
    function isMail()     { return mailChoice().indexOf('Print and mail') === 0; }
    function mailQty()    { return parseInt(fieldVal('mailqty'), 10) || 0; }

    /* Print quantity comes from whichever Apippa quantity input is VISIBLE.
       The conditional-display rules hide the other sizes but leave them in the
       DOM at their own minimum, so reading them all would give nonsense. */
    function activeQtyInput() {
      var els = document.querySelectorAll('input[name^="properties[Quantity"]');
      for (var i = 0; i < els.length; i++) {
        if (els[i].offsetParent !== null) return els[i];
      }
      return null;
    }
    function printQty() {
      var el = activeQtyInput();
      return el ? (parseInt(el.value, 10) || 0) : 0;
    }
    function printCap() {
      var el = activeQtyInput();
      var m = el ? parseInt(el.getAttribute('max'), 10) : 0;
      return m || 0;
    }

    /* --- conditional reveals ---
       Every control that can open a panel carries `data-cdof-choice`, so this
       is one selector rather than a list that has to be kept in step with the
       markup. A select with no `data-cdof-choice` renders perfectly and reveals
       nothing — that is the failure mode to look for if a helper panel stops
       appearing. */
    function syncReveals() {
      var on = [];
      Array.prototype.forEach.call(root.querySelectorAll('[data-cdof-choice]'), function (el) {
        if (el.value) on.push(el.value);
      });
      Array.prototype.forEach.call(root.querySelectorAll('input[type=radio]:checked'), function (el) {
        on.push(el.value);
      });
      Array.prototype.forEach.call(root.querySelectorAll('[data-cdof-reveal]'), function (el) {
        el.hidden = on.indexOf(el.getAttribute('data-cdof-reveal')) === -1;
      });

      /* E5's max is 97% of print quantity — the 3% headroom is a spoilage
         buffer for the mail process (Greg, 15 Sep). Enforced here in the
         HTML max attribute and again in check() for defense-in-depth. */
      var mq = root.querySelector('[data-cdof-mailqty]');
      if (mq) {
        var pq = printQty();
        var maxMail = pq > 0 ? Math.floor(pq * 0.97) : 0;
        if (maxMail > 0) mq.setAttribute('max', String(maxMail));
        else mq.removeAttribute('max');
        var hint = root.querySelector('[data-cdof-mailqty-hint]');
        if (hint) hint.textContent = pq > 0
          ? 'You are printing ' + num(pq) + '. Max mail is ' + num(maxMail) + ' — we recommend printing ~3% more than mail for spoilage in the mail process.'
          : '';
      }
    }

    /* --- route-outs (§2.11) ---
       Eight gates. Evaluated in TABLE ORDER and the first match wins, so the
       customer gets the most fundamental objection rather than the narrowest
       one: quantity beats rush. */
    var GATE_ORDER = [
      'cd_designs',      /* 1 */
      'cd_sources_list', /* 2 */
      'split_drop',      /* 3 */
      'mail_class',      /* 4 */
      'own_permit',      /* 5 */
      'over_cap',        /* 6 */
      'under_min',       /* 7 */
      'rush_too_big'     /* 8 */
    ];

    function fired() {
      var f = {};
      /* Gate 1 routes the order out on a single-piece product. On a package it
         does not: one of five pieces wanting design should not bounce an
         otherwise complete order (Greg, 9 Sep). The choice is recorded in the
         slot's answer instead. */
      if (!MULTI && artwork() === 'Campaign Direct designs it for me') f.cd_designs = true;
      if (CFG.mailEligible && isMail()) {
        if (mailData() === 'Campaign Direct sources the list for me') f.cd_sources_list = true;

        /* Gate 3 is the SPLIT DROP — one piece mailed in stages. Greg, 21 Aug:
           this is NOT a multi-piece program. A program is several different
           pieces run together, it spans products so it cannot be a field on one
           product page, and it is the thing the discount discussions were
           about. An earlier version of this file conflated the two and its
           route-out text told split-drop customers about program pricing. */
        if (mailDrops().indexOf('Split drop') === 0) f.split_drop = true;

        if (mailClass() === 'Non-profit' || mailClass() === 'First class') f.mail_class = true;
        if (mailPermit() === 'Use my own permit') f.own_permit = true;
        if (CFG.mailHouseMinimum > 0 && mailQty() > 0 && mailQty() < CFG.mailHouseMinimum) f.under_min = true;
      }
      var cap = printCap();
      if (cap > 0 && printQty() > cap) f.over_cap = true;
      if (turnaround().indexOf('Rush') === 0 && CFG.rushCeiling > 0 && printQty() > CFG.rushCeiling) f.rush_too_big = true;
      return f;
    }

    function firstGate() {
      var f = fired();
      for (var i = 0; i < GATE_ORDER.length; i++) {
        if (f[GATE_ORDER[i]]) return GATE_ORDER[i];
      }
      return null;
    }

    function tokens(s) {
      return String(s || '')
        .replace('{cap}', num(printCap() || 0))
        .replace('{min}', num(CFG.mailHouseMinimum || 0));
    }

    function showRouteOut(key) {
      if (!key) { routeEl.hidden = true; return; }
      var g = (CFG.gates || {})[key] || {};
      root.querySelector('[data-cdof-routeout-head]').textContent   = CFG.routeOutHeading;
      root.querySelector('[data-cdof-routeout-reason]').textContent = tokens(g.panel || g.reason);
      root.querySelector('[data-cdof-routeout-tail]').textContent   = CFG.routeOutTail;
      routeEl.hidden = false;
    }

    function showError(msg) {
      if (!msg) { errEl.hidden = true; errEl.textContent = ''; errEl.style.cssText = ''; return; }
      errEl.textContent = msg;
      errEl.hidden = false;
      /* Ensure prominent styling even if the block's CSS is subtle. Red-tinted
         callout that stands out visually so the customer sees WHY the add
         failed (Greg, 15 Sep). */
      errEl.style.cssText = 'padding:14px 16px; margin:12px 0; background:#FDECEC; border-left:4px solid #C93838; color:#8B1A1A; font-weight:600; border-radius:4px; line-height:1.4;';
    }

    /* === UPLOADS (8 Sep, Option A) ===
       Native Shopify line-item file properties, for artwork and the mailing
       list. Proven 8 Sep: this product form is a REAL multipart POST, not an
       AJAX add, so a file input submits natively. No custom endpoint.

       WHY THE MARKUP IS BUILT HERE AND NOT IN cd-order-flow.liquid.
       Deliberate, and a documented exception to the block's §2.14 rule that
       every customer-facing string is a schema setting. Editing the 30KB block
       to add two inputs meant re-transmitting the whole file, which is a much
       larger corruption surface than the feature is worth. The three strings
       below are the whole cost. MIGRATE THEM TO SCHEMA SETTINGS the next time
       that block is edited for another reason — then read them from CFG here.

       WHY THE INPUTS MOVE. A file only reaches Shopify if its input is inside
       the product form at submit time, and this widget is a SIBLING block, not
       a descendant of the form. So inject() moves the node into the form and
       moves it back afterwards. That relocation is also the on/off switch: an
       input sitting in the widget is not submitted, which is what stops a stale
       file riding along after the customer changes artwork route.

       WHY THE NAMES START WITH AN UNDERSCORE. Shopify then hides them from the
       cart, the checkout and every notification email, while the order admin
       still shows them. Greg, 8 Sep: the customer must NOT receive the file
       link. Shopify stores uploads at a PUBLIC cdn.shopify.com URL with no
       authentication, and order confirmations get forwarded to consultants,
       treasurers and vendors. Dropping either underscore publishes a campaign's
       unmailed artwork, and its voter file, to everyone in that chain.

       INTERIM. Secure, larger uploads are coming (Greg, 8 Sep). The 20MB cap is
       Shopify's, not ours — above it Shopify rejects the file itself, so raising
       MAX_MB just swaps our written message for a browser error. */
    /* --- PER-PIECE ARTWORK SLOTS (10 Sep) ---
       A package is ONE product containing several printed pieces, each with
       its own artwork and its own route. layout/theme.liquid emits the list
       from the product's cdops.artwork_slots metafield; see the comment there.

       NO SLOTS (null/empty/absent) = LEGACY SINGLE-SLOT MODE, which is every
       product except the packages, and which behaves EXACTLY as before. All
       the multi-slot code below is dead on those pages. That is deliberate:
       the single-product path is the one that has been cart-tested since
       August and it should not shift underneath a package feature.

       GATES BECOME NOTES IN MULTI MODE (Greg, 9 Sep). On a package, choosing
       "Campaign Direct designs it for me" does NOT route the order out, and a
       slot left without a file does NOT block the add. Both record a note in
       the property value instead, so the order carries its own explanation.
       Single-slot products keep the original route-out and the original
       requirement — gate 1 is [LOCKED] in the spec and is not being unlocked
       store-wide off a package conversation. */
    var UP_MAX_SLOTS = 5;

    function readSlots() {
      var el = document.querySelector('script[data-cd-artwork-slots]');
      if (!el) return [];
      var raw;
      try { raw = JSON.parse(el.textContent); } catch (e) { return []; }
      if (!raw || !raw.length) return [];
      var out = [];
      for (var i = 0; i < raw.length && out.length < UP_MAX_SLOTS; i++) {
        var v = String(raw[i] || '').trim();
        if (v) out.push(v);
      }
      return out;
    }

    var SLOTS = readSlots();
    var MULTI = SLOTS.length > 0;

    /* Rates and the size->class map are emitted by layout/theme.liquid from
       metafields - see the comment there for why they are data, not code.
       Anything missing or malformed means POSTAGE is null and no estimate
       renders anywhere. That is the intended off switch. */
    function readPostage() {
      var el = document.querySelector('script[data-cd-postage]');
      if (!el) return null;
      var d;
      try { d = JSON.parse(el.textContent); } catch (e) { return null; }
      if (!d || !d.rates || !d.classes) return null;
      return d;
    }
    var POSTAGE = readPostage();

    /* The five artwork routes. VALUES ARE COPIED FROM THE BLOCK'S OPTION LIST
       AND MUST STAY IDENTICAL — cd-order-flow.js and the gates match several of
       them as exact strings, and the plain-named ones are written to the order
       where a human reads them. See the block's doc header. */
    var ROUTES = [
      { v: '', t: 'Choose one…' },
      { v: 'Upload my print-ready file', t: 'Upload my print-ready file' },
     { v: "I'll send my artwork later by email.", t: "I'll send my artwork later by email." },
      { v: 'Share a Canva link', t: 'Share a Canva link' },
      { v: 'Campaign Direct designs it for me', t: 'Campaign Direct designs it for me' }
    ];

    var SLOT_COPY = {
      intro: 'This package has more than one printed piece. Tell us how the artwork for each one reaches us — they can take different routes.',
      toFollow: ' (file to follow)',
      designNote: " (we'll quote design separately)",
      pending: ' (artwork to follow)'
    };

    var UP_MAX_MB = 20;
    var UP_COPY = {
      artHelp:  "Bigger than that? Choose the email option above and send it after you order — your order confirmation has the address.",
      listHelp: "CSV or Excel, up to 20MB. We'll clean, dedupe, CASS-certify and run NCOA on it before anything mails.",
      tooBig:   "That file is over 20MB, which is more than the upload can take. Choose the email option above and send it after you order — your order confirmation has the address.",
      guideLead: ' Not sure your file is ready? ',
      guideLink: 'See the Artwork Guide.'
    };

    /* POSTAGE ESTIMATE COPY (Andrew's rates, 10 Sep). Never presented as a
       fixed rate: the estimate is a presort average and a small, dispersed
       list can land above it. "Typically lower" is the honest direction and
       is also the one that does not generate an angry phone call.
       The card surcharge is named but NOT quantified (Greg, 10 Sep) - most
       postage is paid by ACH, and printing a percentage here would invite
       arithmetic against an invoice that has not been built yet. */
    var POSTAGE_COPY = {
      lead: 'Estimated postage: ',
      tail: '. Actual is typically lower after list processing for density and sort quality — the final rate is confirmed before your drop. An additional charge may apply if you pay postage by credit card.'
    };

    /* MAIL HOUSE TIERS — Pacific's per-piece rate × 1.25 markup + setup × 1.25.
       Sourced from Pacific Pricing Sheet 2 (postcard tabs) confirmed with Greg
       15 Sep. Setup fee applies at every tier. Below 1,000 the mail house
       cannot run a presort (route-out gate 7), so no rate is defined. */
    var MAILHOUSE_TIERS = [
      { min: 1000,   max: 39999,    rate: 0.09375, setup: 93.75 },
      { min: 40000,  max: 49999,    rate: 0.0875,  setup: 93.75 },
      { min: 50000,  max: 59999,    rate: 0.08125, setup: 93.75 },
      { min: 60000,  max: 79999,    rate: 0.075,   setup: 93.75 },
      { min: 80000,  max: 99999,    rate: 0.06875, setup: 93.75 },
      { min: 100000, max: Infinity, rate: 0.0625,  setup: 93.75 }
    ];
    var UP_KINDS = {
      art:  { prop: '_Artwork File', reveal: 'Upload my print-ready file',
              accept: '.pdf,.ai,.eps,.psd,.tif,.tiff,.jpg,.jpeg,.png,.zip', help: UP_COPY.artHelp },
      list: { prop: '_Mailing List',  reveal: "I'll upload my list",
              accept: '.csv,.tsv,.txt,.xls,.xlsx,.zip',                     help: UP_COPY.listHelp }
    };

    /* The Artwork Guide link (Greg, 8 Sep: "upload instructions need a link to
       the art guide page"). Built as a real node rather than innerHTML — the
       help text is set with textContent and there is no reason to start
       parsing HTML in this file for one anchor. */
    function guideLink() {
      var frag = document.createDocumentFragment();
      frag.appendChild(document.createTextNode(UP_COPY.guideLead));
      var a = document.createElement('a');
      a.href = '/pages/artwork-guide';
      a.textContent = UP_COPY.guideLink;
      frag.appendChild(a);
      return frag;
    }

    function mountUploads() {
      if (root.querySelector('[data-cdof-uploads]')) return;
      var css = document.createElement('style');
      css.setAttribute('data-cdof-uploads', '');
      css.textContent =
        '.cdof__filerow{margin-block-start:.5rem}' +
        '.cdof__file{display:block;width:100%;max-width:30rem;font-size:.9rem;color:inherit}' +
        '.cdof__file::file-selector-button{margin-inline-end:.6rem;padding:.4rem .7rem;' +
        'border:1px solid rgb(128 128 128 / .45);border-radius:4px;background:#F2F5F9;' +
        'color:#0A1E4A;font-family:inherit;font-size:.88rem;font-weight:600;cursor:pointer}';
      root.appendChild(css);

      if (MULTI) mountSlots();

      Object.keys(UP_KINDS).forEach(function (kind) {
        var spec = UP_KINDS[kind];
        /* The legacy single artwork input is replaced by the slot rows. */
        if (kind === 'art' && MULTI) return;
        var panel = root.querySelector('[data-cdof-reveal="' + spec.reveal.replace(/"/g, '\\"') + '"]');
        if (!panel) return;

        var home = document.createElement('div');
        home.className = 'cdof__filerow';
        home.setAttribute('data-cdof-' + kind + 'home', '');

        var inp = document.createElement('input');
        inp.type = 'file';
        inp.className = 'cdof__file';
        inp.name = 'properties[' + spec.prop + ']';
        inp.setAttribute('accept', spec.accept);
        inp.setAttribute('data-cdof-' + kind + 'file', '');
        home.appendChild(inp);

        var help = document.createElement('p');
        help.className = 'cdof__help';
        help.textContent = spec.help;
        if (kind === 'art') help.appendChild(guideLink());

        /* Before any pending note, so the note reads as a footnote to the
           control rather than a replacement for it. */
        var note = panel.querySelector('.cdof__note');
        if (note) { panel.insertBefore(home, note); panel.insertBefore(help, note); }
        else { panel.appendChild(home); panel.appendChild(help); }
      });
    }

    /* In multi mode the artwork inputs are keyed art1..art5; 'art' alone is the
       legacy single input and 'list' is the mailing list in both modes. */
    function artKey(i) { return MULTI ? ('art' + i) : 'art'; }

    /* Replaces the block's single artwork dropdown with one row per piece.
       The block's own select is hidden rather than removed, so nothing that
       queries [data-cdof-artwork] breaks and the change is reversible by
       deleting the metafield. */
    function mountSlots() {
      var host = root.querySelector('[data-cdof-artwork]');
      if (!host) return;
      var group = host.closest('.cdof__group') || host.parentNode;
      if (group.querySelector('[data-cdof-slots]')) return;

      host.hidden = true;
      host.removeAttribute('data-cdof-choice');
      /* The legacy reveals are keyed by ROUTE VALUE, and a slot select carries
         those same values — so leaving data-cdof-reveal in place would make
         syncReveals() re-open the old single-artwork panel (and its orphaned
         file input) the moment any slot chose Upload. Strip the attribute so
         only the slot rows respond. Scoped to the artwork group, so the mail
         reveals are untouched. */
      Array.prototype.forEach.call(group.querySelectorAll('.cdof__reveal'), function (el) {
        el.hidden = true;
        el.removeAttribute('data-cdof-reveal');
        el.setAttribute('data-cdof-legacy-reveal', '');
      });

      var wrap = document.createElement('div');
      wrap.setAttribute('data-cdof-slots', '');

      var intro = document.createElement('p');
      intro.className = 'cdof__help';
      intro.textContent = SLOT_COPY.intro;
      intro.appendChild(guideLink());
      wrap.appendChild(intro);

      SLOTS.forEach(function (label, idx) {
        var i = idx + 1;
        var row = document.createElement('div');
        row.className = 'cdof__sub';

        var lab = document.createElement('label');
        lab.className = 'cdof__inlabel';
        lab.textContent = label;
        lab.setAttribute('for', root.id + '-slot' + i);
        row.appendChild(lab);

        var sel = document.createElement('select');
        sel.className = 'cdof__select';
        sel.id = root.id + '-slot' + i;
        sel.setAttribute('data-cdof-slot', String(i));
        /* Deliberately NOT data-cdof-choice: syncReveals() collects those values
           to drive [data-cdof-reveal] panels, and a slot's value could collide
           with a reveal name. syncSlots() handles these rows instead. */
        ROUTES.forEach(function (o) {
          var opt = document.createElement('option');
          opt.value = o.v;
          opt.textContent = o.t;
          sel.appendChild(opt);
        });
        row.appendChild(sel);

        var fileWrap = document.createElement('div');
        fileWrap.className = 'cdof__filerow';
        fileWrap.setAttribute('data-cdof-art' + i + 'home', '');
        fileWrap.hidden = true;
        var inp = document.createElement('input');
        inp.type = 'file';
        inp.className = 'cdof__file';
        inp.name = 'properties[_Artwork File ' + i + ']';
        inp.setAttribute('accept', UP_KINDS.art.accept);
        inp.setAttribute('data-cdof-art' + i + 'file', '');
        fileWrap.appendChild(inp);
        row.appendChild(fileWrap);

        var canvaWrap = document.createElement('div');
        canvaWrap.className = 'cdof__filerow';
        canvaWrap.hidden = true;
        canvaWrap.setAttribute('data-cdof-slotcanvarow', String(i));
        var cin = document.createElement('input');
        cin.className = 'cdof__input';
        cin.type = 'url';
        cin.setAttribute('inputmode', 'url');
        cin.setAttribute('autocomplete', 'off');
        cin.setAttribute('placeholder', 'https://www.canva.com/design/...');
        cin.setAttribute('data-cdof-slotcanva', String(i));
        canvaWrap.appendChild(cin);
        row.appendChild(canvaWrap);

        var note = document.createElement('p');
        note.className = 'cdof__help';
        note.setAttribute('data-cdof-slotnote', String(i));
        note.hidden = true;
        row.appendChild(note);

        wrap.appendChild(row);
      });

      group.appendChild(wrap);
      syncSlots();
    }

    /* Show the right sub-control for each slot's chosen route, and surface the
       note inline so the customer sees what we will do rather than being
       stopped. */
    function syncSlots() {
      if (!MULTI) return;
      SLOTS.forEach(function (label, idx) {
        var i = idx + 1;
        var r = slotRoute(i);
        var fh = root.querySelector('[data-cdof-art' + i + 'home]');
        var cv = root.querySelector('[data-cdof-slotcanvarow="' + i + '"]');
        var nt = root.querySelector('[data-cdof-slotnote="' + i + '"]');
        if (fh) fh.hidden = r !== 'Upload my print-ready file';
        if (cv) cv.hidden = r !== 'Share a Canva link';
        if (!nt) return;
        var msg = '';
        if (r === 'Campaign Direct designs it for me') {
          msg = "We'll quote the design for this piece separately. The rest of your order goes through as normal.";
        } else if (r === "I'll send my artwork later by email.") {
          msg = "We'll email you where to send this piece after checkout.";
        } else if (r === 'Upload my print-ready file' && !fileOf(artKey(i))) {
          msg = 'No file chosen yet — you can add it now or email it later. We will not print this piece until it arrives.';
        }
        nt.textContent = msg;
        nt.hidden = !msg;
      });
    }

    /* The Size the customer has chosen, read from APIPPA's own select. The
       class map is keyed on these exact strings, which is the same
       load-bearing-label dependency the gates have. */
    function currentSize() {
      var s = document.querySelector('select[name="properties[Size]"]');
      return s ? String(s.value) : '';
    }

    /* Returns null - meaning SHOW NOTHING - whenever anything is unknown:
       no data, an unmapped size, a missing rate, no mail quantity yet.
       Deliberate. Quoting the letter rate on a flat understates postage by
       about 88%, so silence is the only safe failure. */
    function postageEstimate() {
      if (!POSTAGE) return null;
      var cls = POSTAGE.classes[currentSize()];
      if (!cls) return null;
      var rate = POSTAGE.rates[cls];
      if (typeof rate !== 'number' || !(rate > 0)) return null;
      var q = mailQty();
      if (!q) return null;
      return { cls: cls, rate: rate, qty: q, total: q * rate };
    }

    /* Mail-house prep — pieces-per-tier rate + flat setup, marked up 1.25 from
       Pacific. Returns null below 1,000 (route-out gate 7 catches it). Drop
       and freight from mail house to the entry point are NOT included — those
       vary by drop location and are quoted with the postage invoice. */
    function mailhouseEstimate() {
      var q = mailQty();
      if (!q || q < 1000) return null;
      for (var i = 0; i < MAILHOUSE_TIERS.length; i++) {
        var t = MAILHOUSE_TIERS[i];
        if (q >= t.min && q <= t.max) {
          return { qty: q, rate: t.rate, setup: t.setup, total: q * t.rate + t.setup };
        }
      }
      return null;
    }

    function money(n) {
      var v = (Math.round(n * 100) / 100).toFixed(2).split('.');
      return '$' + num(v[0]) + '.' + v[1];
    }

    function mountPostage() {
      if (!POSTAGE) return;
      if (root.querySelector('[data-cdof-postage]')) return;
      var mq = root.querySelector('[data-cdof-mailqty]');
      if (!mq) return;
      var host = (mq.closest && mq.closest('.cdof__sub')) || mq.parentNode;
      /* Prominent callout instead of a help paragraph (Greg, 15 Sep). Cream
         background + gold accent draws the eye; "Invoiced separately from
         checkout" prevents confusion with the card total. */
      var p = document.createElement('div');
      p.className = 'cdof__postage-callout';
      p.setAttribute('data-cdof-postage', '');
      p.hidden = true;
      p.style.cssText = 'margin:12px 0 8px; padding:14px 16px; background:#F2E9D4; border-left:4px solid #D4981A; border-radius:4px; color:#0A1E4A; line-height:1.5;';
      host.appendChild(p);
    }

    function syncPostage() {
      var el = root.querySelector('[data-cdof-postage]');
      if (!el) return;
      var isMailing = CFG.mailEligible && isMail();
      var pe = isMailing ? postageEstimate() : null;
      if (!pe) { el.hidden = true; el.innerHTML = ''; return; }
      /* Bundled display (Greg 15 Sep): postage + mail prep combined. Drop and
         freight to the entry point aren't included — those vary by location
         and are quoted with the postage invoice. */
      var mh = mailhouseEstimate();
      var combined = pe.total + (mh ? mh.total : 0);
      el.innerHTML =
        '<div style="font-size:17px; font-weight:700;">Estimated mailing costs: ' + money(combined) + '</div>' +
        '<div style="font-size:13px; margin-top:6px; color:#33405E; font-weight:400;">' +
        'Postage plus mail prep. Postage passed through at cost. ' +
        '<strong>Invoiced separately</strong> from checkout total after your list is processed. ' +
        'Actual is typically lower after list processing for density and sort quality — the final rate is confirmed before your drop. An additional charge may apply if you pay postage by credit card.' +
        '</div>';
      el.hidden = false;
    }

    function fileEl(kind) { return root.querySelector('[data-cdof-' + kind + 'file]'); }
    function fileOf(kind) {
      var el = fileEl(kind);
      return el && el.files && el.files.length ? el.files[0] : null;
    }
    function tooBig(f) { return !!f && f.size > UP_MAX_MB * 1024 * 1024; }
    function wantsUpload() { return artwork() === 'Upload my print-ready file'; }

    /* --- multi-slot reads --- */
    function slotSel(i) { return root.querySelector('[data-cdof-slot="' + i + '"]'); }
    function slotRoute(i) { var el = slotSel(i); return el ? el.value : ''; }
    function slotCanva(i) {
      var el = root.querySelector('[data-cdof-slotcanva="' + i + '"]');
      return el ? String(el.value).trim() : '';
    }
    /* The value written to the order. The note IS the value — no separate flag
       for anyone to miss. Reads acceptably to a customer as well as to us,
       because these strings reach the confirmation email. */
    function slotAnswer(i) {
      var r = slotRoute(i);
      if (!r) return SLOTS[i - 1] + SLOT_COPY.pending;
      if (r === 'Upload my print-ready file' && !fileOf(artKey(i))) return r + SLOT_COPY.toFollow;
      if (r === 'Campaign Direct designs it for me') return r + SLOT_COPY.designNote;
      return r;
    }
    function wantsListUpload() {
      return CFG.mailEligible && isMail() && mailData() === "I'll upload my list";
    }

    /* Idempotent, and called in both directions on every passing click. */
    function putFileIn(kind, on) {
      var el = fileEl(kind);
      if (!el) return;
      var f = form();
      var home = root.querySelector('[data-cdof-' + kind + 'home]');
      if (on && f) {
        /* It is in the form only to be submitted — it must not render there,
           beside Add to cart, outside its own panel. */
        if (el.parentNode !== f) f.appendChild(el);
        el.style.display = 'none';
      } else {
        if (home && el.parentNode !== home) home.appendChild(el);
        el.style.display = '';
      }
    }

    function no(msg) { return { ok: false, msg: msg }; }

    function check() {
      if (MULTI) {
        /* Each piece needs a ROUTE chosen — that is how we know what happens
           next. The FILE is not required (Greg, 9 Sep); a missing one is noted
           and can follow by email. Canva links are still validated, because a
           broken link costs a day either way. */
        for (var si = 1; si <= SLOTS.length; si++) {
          if (!slotRoute(si)) {
            return no('Tell us how the artwork for ' + SLOTS[si - 1] + ' reaches us.');
          }
          if (slotRoute(si) === 'Share a Canva link') {
            var cu = slotCanva(si);
            if (!cu) return no('Paste the Canva link for ' + SLOTS[si - 1] + '.');
            if (!/^https?:\/\/(www\.)?canva\.com\//i.test(cu)) {
              return no('That does not look like a Canva link for ' + SLOTS[si - 1] + '. It should start with https://www.canva.com/design/');
            }
          }
          var sf = fileOf(artKey(si));
          if (tooBig(sf)) return no(UP_COPY.tooBig);
        }
      } else if (!artwork()) {
        return no('Tell us how your artwork will reach us before adding this to the cart.');
      }

      /* A fired gate blocks the add; the route-out panel carries the message, so
         there is deliberately no error line as well. */
      if (firstGate()) return { ok: false, msg: '' };

      if (!MULTI && artwork() === 'Share a Canva link') {
        var u = fieldVal('canva');
        if (!u) return no('Paste your Canva link, or choose another way to send us your artwork.');
        if (!/^https?:\/\/(www\.)?canva\.com\//i.test(u)) {
          return no('That does not look like a Canva link. It should start with https://www.canva.com/design/');
        }
      }

      /* GATE 9 — an upload route with nothing uploaded. Without this a customer
         picks "upload", adds to cart with no file, and the order looks complete
         to whoever fulfils it. */
      if (!MULTI && wantsUpload()) {
        var af = fileOf('art');
        if (!af) return no('Choose your print-ready file, or pick another way to send us your artwork.');
        if (tooBig(af)) return no(UP_COPY.tooBig);
      }

      /* §7.2 cases 9 and 10 — a mail order stands alone. This is what makes the
         order-level cdops metafields safe: one drop date, one list, one permit,
         one postage invoice per order. */
      if (cart.hasMailLine) return no(CFG.mailAloneMessage);

      if (CFG.mailEligible) {
        if (!mailChoice()) return no('Choose print only, or print and mail.');
        if (isMail()) {
          if (cart.count > 0) return no(CFG.mailAloneMessage);
          if (!mailQty()) return no('Tell us how many pieces are going in the mail.');
          var pq = printQty();
          if (pq > 0) {
            var maxMailCheck = Math.floor(pq * 0.97);
            if (mailQty() > maxMailCheck) {
              return no('Mail cannot exceed 97% of print (3% spoilage buffer). You are printing ' + num(pq) + ' — max mail is ' + num(maxMailCheck) + '. Increase print or lower mail.');
            }
          }
          if (!mailData())   return no('Tell us where the mailing list comes from.');
          if (wantsListUpload()) {
            var lf = fileOf('list');
            if (!lf) return no('Choose your list file, or pick another way to get it to us.');
            if (tooBig(lf)) return no(UP_COPY.tooBig);
          }
          if (!mailDrops())  return no('Tell us whether the whole quantity mails at once.');
          if (!mailClass())  return no('Tell us how it is mailing.');
          if (!mailPermit()) return no('Tell us which permit it mails under.');
          if (!fieldVal('dropdate')) {
            return no('Give us a drop date — the day your mail enters the postal stream.');
          }
        }
      }

      return { ok: true, msg: '' };
    }

    /* --- property injection ---
       Hidden inputs appended to the product form. Verified 21 Aug that a
       properties[...] input appended to this form by non-Apippa code reaches the
       cart line intact, alongside Apippa's own.
       Underscore-prefixed names are hidden from the customer-facing cart and
       visible in admin; the plain ones are what a human reads on the order, so
       these strings ARE the order paperwork. Do not reword them casually. */
    function setProp(name, value) {
      var f = form();
      if (!f) return;
      var el = f.querySelector('input[data-cdof-prop="' + name + '"]');
      if (value === null || value === '' || value === undefined) {
        if (el) el.parentNode.removeChild(el);
        return;
      }
      if (!el) {
        el = document.createElement('input');
        el.type = 'hidden';
        el.setAttribute('data-cdof-prop', name);
        f.appendChild(el);
      }
      el.name  = 'properties[' + name + ']';
      el.value = value;
    }

    /* Written unambiguously rather than as 10/09/2026, because this string is
       read by a print shop and a mail house, not by a browser. */
    function prettyDate(iso) {
      var m = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      var p = String(iso).split('-');
      if (p.length !== 3) return iso;
      return m[parseInt(p[1], 10) - 1] + ' ' + parseInt(p[2], 10) + ', ' + p[0];
    }

    function inject() {
      var a = artwork();
      setProp('Artwork', a);
      setProp('Turnaround', turnaround());
      setProp('_artwork_url',    a === 'Share a Canva link' ? fieldVal('canva') : null);
      setProp('_artwork_source', a === 'Share a Canva link' ? 'canva_link' : null);

      if (MULTI) {
        /* One visible answer per piece (label in the KEY, note in the VALUE) and
           one indexed file property per piece (index in the KEY, because Flow
           cannot count loop iterations and needs a fixed key per metafield). */
        setProp('Artwork', null);
        SLOTS.forEach(function (label, idx) {
          var i = idx + 1;
          var r = slotRoute(i);
          setProp('Artwork — ' + label, slotAnswer(i));
          setProp('_artwork_url ' + i, r === 'Share a Canva link' ? slotCanva(i) : null);
          putFileIn(artKey(i), r === 'Upload my print-ready file' && !!fileOf(artKey(i)));
        });
      } else {
        var up = wantsUpload();
        putFileIn('art', up);
        if (up) setProp('_artwork_source', 'upload');
      }

      var mail = CFG.mailEligible && isMail();
      setProp('Mail Choice',   CFG.mailEligible ? mailChoice() : null);
      setProp('Mail Quantity', mail ? num(mailQty()) : null);
      setProp('Mail Data',     mail ? mailData() : null);
      setProp('Mail Drops',    mail ? mailDrops() : null);
      setProp('Mail Class',    mail ? mailClass() : null);
      setProp('Mail Permit',   mail ? mailPermit() : null);
      setProp('Drop Date',     mail ? prettyDate(fieldVal('dropdate')) : null);
      /* Underscore-prefixed: this is an ESTIMATE and must not appear in the
         confirmation email, where it would read as a quote. Fulfilment sees
         it on the order; the customer sees it only on the product page. */
      var pe = mail ? postageEstimate() : null;
      setProp('_postage_estimate', pe
        ? money(pe.total) + ' est. (' + num(pe.qty) + ' ' + pe.cls + ' @ $' + pe.rate.toFixed(3) + '/pc)'
        : null);
      var mh = mail ? mailhouseEstimate() : null;
      setProp('_mail_prep_estimate', mh
        ? money(mh.total) + ' est. (' + num(mh.qty) + ' @ $' + mh.rate.toFixed(5) + '/pc + $' + mh.setup.toFixed(2) + ' setup)'
        : null);
      putFileIn('list', wantsListUpload());
    }

    /* --- THE VETO ---
       On a pass we inject and RETURN — the same click carries on to Apippa's
       handler, which merges its own properties and submits. No re-dispatch. */
    document.addEventListener('click', function (e) {
      var btn = e.target && e.target.closest ? e.target.closest('[name="add"]') : null;
      if (!btn) return;

      /* Skip buttons belonging to a DIFFERENT product form — the quick-add
         buttons in the recommendations section. A button with no form of its own
         (the mobile sticky add-to-cart bar) is still ours and is still gated:
         that is why this tests ownership rather than containment. */
      var f = form();
      var owner = btn.closest('form');
      if (owner && f && owner !== f) return;

      var verdict = check();
      if (verdict.ok) { showError(null); inject(); return; }

      e.preventDefault();
      e.stopImmediatePropagation();
      showError(verdict.msg);
      showRouteOut(firstGate());
      /* Scroll to what the customer needs to see, not the top of the widget.
         Root scrolled them past the message on longer product pages —
         Apippa's button animation runs anyway, so they thought the add
         succeeded (Greg, 15 Sep). Error message wins, then route-out panel,
         widget root only as fallback. */
      var scrollTarget = (errEl && !errEl.hidden) ? errEl
                       : (routeEl && !routeEl.hidden) ? routeEl
                       : root;
      scrollTarget.scrollIntoView({ block: 'center', behavior: 'smooth' });
      /* Move focus to the first unanswered visible control so keyboard and
         screen-reader users know what needs their attention. preventScroll so
         the scrollIntoView above still decides where the page lands. */
      var candidates = root.querySelectorAll('select, input[type=url], input[type=file], input[type=date], input[type=number]');
      for (var fi = 0; fi < candidates.length; fi++) {
        if (!candidates[fi].value && candidates[fi].offsetParent !== null) {
          candidates[fi].focus({ preventScroll: true });
          break;
        }
      }
      /* Self-heal: if this block was decided on stale cart state, the next click
         will be decided on fresh state. See the note on `cart` above. */
      refreshCart();
    }, true);

    /* Belt and braces: if the add path ever changes to one that DOES fire a
       submit event, this catches it too. Harmless today — it never fires, which
       is the whole finding. */
    document.addEventListener('submit', function (e) {
      var f = form();
      if (!f || e.target !== f) return;
      var verdict = check();
      if (verdict.ok) { inject(); return; }
      e.preventDefault();
      e.stopImmediatePropagation();
      showError(verdict.msg);
      refreshCart();
    }, true);

    /* --- wiring --- */
    mountUploads();
    mountPostage();

    root.addEventListener('change', function () {
      syncSlots();
      syncReveals();
      syncPostage();
      updateDropDateMin();
      showRouteOut(firstGate());
      showError(null);
      refreshCart();
    });
    root.addEventListener('input', function () { syncSlots(); syncPostage(); showError(null); });

    /* The calculator owns print quantity and size, and both feed this widget:
       E5's ceiling and gates 6 and 8. Listen at document level because those
       inputs are Apippa's and live outside this block. */
    document.addEventListener('change', function (e) {
      var n = e.target && e.target.name;
      if (n && n.indexOf('properties[') === 0) { syncReveals(); syncPostage(); showRouteOut(firstGate()); }
    }, true);
    document.addEventListener('input', function (e) {
      var n = e.target && e.target.name;
      if (n && n.indexOf('properties[Quantity') === 0) { syncReveals(); syncPostage(); showRouteOut(firstGate()); }
    }, true);

    /* Drop date minimum: 5 business days for Standard, 3 business days for
       Rush. Business days skip weekends. Called on init and on turnaround
       change, so picking Rush unlocks earlier drop dates.
       (2026-09-15, Greg — supersedes CFG.earliestDropOffsetDays.) */
    function businessDaysFromToday(n) {
      var d = new Date();
      d.setHours(0, 0, 0, 0);
      var added = 0;
      while (added < n) {
        d.setDate(d.getDate() + 1);
        var day = d.getDay();
        if (day !== 0 && day !== 6) added++;
      }
      return d.toISOString().slice(0, 10);
    }

    function updateDropDateMin() {
      var dd = root.querySelector('[data-cdof-dropdate]');
      if (!dd) return;
      var isRush = turnaround().indexOf('Rush') === 0;
      var minDate = businessDaysFromToday(isRush ? 3 : 5);
      dd.min = minDate;
      /* If the customer had a date now below floor (they switched from Rush
         to Standard), clear it so they must re-pick. */
      if (dd.value && dd.value < minDate) dd.value = '';
    }

    syncReveals();
    syncPostage();
    updateDropDateMin();
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-cd-order-flow]'), init);
    refreshCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.addEventListener('pageshow', refreshCart);
  window.addEventListener('focus', refreshCart);
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) refreshCart();
  });
})();